import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/axiosConfig';
import { setError } from './errorSlice';

// Helper para guardar en localStorage de forma segura
const safeLocalStorageSet = (key, value) => {
  const val = value ?? '';
  localStorage.setItem(key, val.toString());
};

// Acción para completar datos pendientes
export const completePendingData = createAsyncThunk(
  'auth/completePendingData',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/complete-data', formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al completar datos');
    }
  }
);

// Acción para solicitar recuperación de contraseña
export const requestPasswordRecovery = createAsyncThunk(
  'auth/requestPasswordRecovery',
  async (email, { rejectWithValue }) => {
    try {
      if (typeof email !== 'string' || email.trim() === '') {
        throw new Error('El correo electrónico es inválido.');
      }
      const response = await api.post('/auth/recover-password', { email: email.trim() });
      return response.data;
    } catch (error) {
      if (error.response?.status === 429) {
        return rejectWithValue('Ya se envió un correo recientemente. Intenta nuevamente en unos minutos.');
      }
      return rejectWithValue(error.response?.data?.errors || 'Error al solicitar recuperación de contraseña');
    }
  }
);

// Acción para restablecer la contraseña
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Error al restablecer la contraseña');
    }
  }
);

// Acción para logout asíncrono
export const logoutAsync = createAsyncThunk(
  'auth/logoutAsync',
  async (_, { rejectWithValue }) => {
    try {
      await api.get('/auth/logout');
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cerrar sesión');
    }
  }
);

// Acción para obtener perfil del usuario
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/auth/profile');
      const { usuario, cambio_clave, solicitar_email_personal, completar_emergencia } = response.data;

      if (completar_emergencia) {
        dispatch(setError({
          message: 'Debes actualizar tu contacto de emergencia y/o teléfono de emergencia.',
          status: 400,
          redirectTo: '/cambio-clave',
        }));
      }

      return {
        ...usuario,
        cambioClave: cambio_clave,
        solicitarEmailPersonal: solicitar_email_personal,
        completarEmergencia: completar_emergencia
      };
    } catch (error) {
      dispatch(logout());
      return rejectWithValue(error.response?.data?.message || 'Error al obtener el perfil');
    }
  }
);

// Acción para actualizar perfil del usuario
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al actualizar el perfil');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    recoveryMessage: null,
    token: localStorage.getItem('token') || null,
    globalMessage: null,
    isAuthenticated: !!localStorage.getItem('token'),
    usuario: {
      id: localStorage.getItem('id') || null,
      nombre: localStorage.getItem('nombre') || '',
      apellido: localStorage.getItem('apellido') || '',
      rol: localStorage.getItem('rol') || '',
      email_personal: localStorage.getItem('email_personal') || '',
      contacto_emergencia: localStorage.getItem('contacto_emergencia') || '',
      telefono_emergencia: localStorage.getItem('telefono_emergencia') || '',
      cambioClave: localStorage.getItem('cambio_clave') === 'true',
      solicitarEmailPersonal: localStorage.getItem('solicitar_email_personal') === 'true',
      completarEmergencia: localStorage.getItem('completar_emergencia') === 'true',
      isAdmin: localStorage.getItem('rol') === 'administrador',
      isSocio: localStorage.getItem('rol') === 'socio',
      comuna: JSON.parse(localStorage.getItem('comuna')) || null ,
    },
  },
  reducers: {
    login: (state, action) => {
      


      const {
        token,
        id,
        rol,
        nombre,
        apellido,
        cambio_clave = false,
        solicitar_email_personal = false,
        completar_emergencia = false,
        email_personal = '',
        contacto_emergencia = '',
        telefono_emergencia = '',
        comuna = null
      } = action.payload;

      // Actualizar estado
      state.token = token;
      state.isAuthenticated = !!token;
      state.isAdmin = rol === 'administrador';
      state.isSocio = rol === 'socio';

      state.usuario = {
        id,
        rol,
        nombre,
        apellido,
        email_personal,
        contacto_emergencia,
        telefono_emergencia,
        cambioClave: cambio_clave,
        solicitarEmailPersonal: solicitar_email_personal,
        completarEmergencia: completar_emergencia,
        comuna
      };

      // Persistir datos
      const authData = {
        token,
        id,
        rol,
        nombre,
        apellido,
        cambio_clave,
        solicitar_email_personal,
        completar_emergencia,
        email_personal,
        contacto_emergencia,
        telefono_emergencia,
        comuna: JSON.stringify(comuna)
      };

      Object.entries(authData).forEach(([key, value]) => {
        safeLocalStorageSet(key, value);
      });

      
      
    },
    logout: (state) => {
      // Resetear estado
      state.token = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.isSocio = false;
      state.usuario = {
        id: null,
        nombre: '',
        apellido: '',
        rol: '',
        email_personal: '',
        contacto_emergencia: '',
        telefono_emergencia: '',
        cambioClave: false,
        solicitarEmailPersonal: false,
        completarEmergencia: false
      };

      // Limpiar almacenamiento
      localStorage.clear();

      
    },
    setGlobalMessage: (state, action) => {
      state.globalMessage = action.payload;
    },
    initializeAuth: (state) => {
      const rol = localStorage.getItem('rol') || '';
      state.isAdmin = rol === 'administrador';
      state.isSocio = rol === 'socio';
      
      // Sincronizar estado con localStorage
      state.token = localStorage.getItem('token');
      state.isAuthenticated = !!state.token;

      state.usuario = {
        id: localStorage.getItem('id'),
        nombre: localStorage.getItem('nombre') || '',
        apellido: localStorage.getItem('apellido') || '',
        rol: localStorage.getItem('rol') || '',
        email_personal: localStorage.getItem('email_personal') || '',
        contacto_emergencia: localStorage.getItem('contacto_emergencia') || '',
        telefono_emergencia: localStorage.getItem('telefono_emergencia') || '',
        cambioClave: localStorage.getItem('cambio_clave') === 'true',
        solicitarEmailPersonal: localStorage.getItem('solicitar_email_personal') === 'true',
        completarEmergencia: localStorage.getItem('completar_emergencia') === 'true',

      };

      
      
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutAsync.fulfilled, (state) => {
        authSlice.caseReducers.logout(state);
      })
      .addCase(logoutAsync.rejected, (state, action) => {
        state.logoutError = action.payload;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        const { id, nombre, apellido, rol, ...profileData } = action.payload; // <-- Añade rol aquí
        state.usuario = {
          ...state.usuario,
          id,
          nombre,
          apellido,
          rol,          // Ahora rol está definido
          isAdmin: rol === 'administrador', // Ahora rol está definido
          ...profileData
        };

        // También actualiza el estado global de isAdmin
        state.isAdmin = rol === 'administrador';

        

        // Actualizar localStorage
        ['id', 'nombre', 'apellido', 'rol', 'cambioClave', 'solicitarEmailPersonal', 'completarEmergencia'].forEach(key => {
          safeLocalStorageSet(key, action.payload[key]);
        });
      })
      .addCase(completePendingData.fulfilled, (state) => {
        state.usuario.cambioClave = false;
        state.usuario.solicitarEmailPersonal = false;
        state.usuario.completarEmergencia = false;

        // Actualizar localStorage
        ['cambio_clave', 'solicitar_email_personal', 'completar_emergencia']
          .forEach(key => localStorage.setItem(key, 'false'));
      })
      .addCase(completePendingData.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.usuario = { ...state.usuario, ...action.payload };
        Object.entries(action.payload).forEach(([key, value]) => {
          safeLocalStorageSet(key, value);
        });
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(requestPasswordRecovery.fulfilled, (state, action) => {
        state.recoveryMessage = action.payload.message;
      })
      .addCase(requestPasswordRecovery.rejected, (state, action) => {
        state.recoveryMessage = action.payload.error || 'Error al solicitar recuperación de contraseña';
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.globalMessage = 'Contraseña restablecida con éxito';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.globalMessage = action.payload;
      });
  },
});

export const { login, logout, setGlobalMessage, initializeAuth } = authSlice.actions;
export default authSlice.reducer; 